/**
 * CivilianRidesCard — embedded inside the Civilian Night flyer to show
 * who's offering lifts + who needs one, plus inline forms so anyone
 * with the link can offer or request a ride without leaving the page.
 *
 * Guests see all the data (read-only) and get the AuthGate on tap.
 *
 * Lives in its own backend collection (`civilian_rides`) so it never
 * interferes with the main ride-share / trips system used by skating
 * events.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Car, Hand, Plus, Loader2, X, MessageCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import AuthGateDialog from '@/components/AuthGateDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeader = () => {
  const t = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Row = ({ avatarUrl, name, sub, right }) => (
  <div className="flex items-center gap-3 p-2.5 rounded-lg bg-card/60 border border-border/40">
    <Avatar className="w-9 h-9">
      <AvatarImage src={avatarUrl || undefined} />
      <AvatarFallback className="bg-fuchsia-600 text-white text-xs">
        {(name || '?').charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold truncate">{name || 'Someone'}</div>
      <div className="text-[11px] text-muted-foreground truncate">{sub}</div>
    </div>
    {right}
  </div>
);

export default function CivilianRidesCard({ civId, isEventEnded, eventName }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [mode, setMode] = useState(null); // 'offer' | 'request' | null
  const [busy, setBusy] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);
  const [gateFeature, setGateFeature] = useState('Civilian rides');

  // form fields
  const [pickup, setPickup] = useState('');
  const [seats, setSeats] = useState(3);
  const [passengers, setPassengers] = useState(1);
  const [contribution, setContribution] = useState('');
  const [notes, setNotes] = useState('');

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/civilian-nights/${civId}/rides`);
      setOffers(data.offers || []);
      setRequests(data.requests || []);
    } catch {
      setOffers([]); setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Inline fetch to satisfy React 19's set-state-in-effect lint —
    // calling `load()` here triggers it because the linter can't see
    // the function's body across the boundary.
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/civilian-nights/${civId}/rides`);
        if (cancelled) return;
        setOffers(data.offers || []);
        setRequests(data.requests || []);
      } catch {
        if (!cancelled) { setOffers([]); setRequests([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [civId]);

  const openForm = (which) => {
    if (!user) {
      setGateFeature(which === 'offer' ? 'offering a ride' : 'requesting a ride');
      setGateOpen(true);
      return;
    }
    if (isEventEnded) {
      toast('This event has ended');
      return;
    }
    setMode(which);
    setPickup(''); setSeats(3); setPassengers(1); setContribution(''); setNotes('');
  };

  const submit = async () => {
    if (!pickup.trim()) {
      toast.error('Add a pickup area');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'offer') {
        await axios.post(`${API}/civilian-nights/${civId}/rides/offer`, {
          pickup_area: pickup.trim(),
          available_seats: Number(seats) || 1,
          contribution: contribution === '' ? null : Number(contribution),
          notes: notes.trim() || null,
        }, { headers: authHeader() });
        toast.success("Lift posted — riders can see it now");
      } else {
        await axios.post(`${API}/civilian-nights/${civId}/rides/request`, {
          pickup_area: pickup.trim(),
          passenger_count: Number(passengers) || 1,
          notes: notes.trim() || null,
        }, { headers: authHeader() });
        toast.success("Ride request posted — drivers can see it now");
      }
      setMode(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not post');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (ride_id) => {
    if (!window.confirm('Remove this ride post?')) return;
    try {
      await axios.delete(`${API}/civilian-nights/${civId}/rides/${ride_id}`, {
        headers: authHeader(),
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove');
    }
  };

  const myId = user?.user_id;
  const offersCount = offers.length;
  const requestsCount = requests.length;

  return (
    <>
      <Card className="bg-card/60 border-border/50 mt-4" data-testid="civilian-rides-card">
        <CardContent className="p-5 space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Car className="w-4 h-4 text-muted-foreground" />
              <h2 className="font-semibold text-sm">
                Rides for this event
              </h2>
            </div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
              {offersCount} offer{offersCount === 1 ? '' : 's'} · {requestsCount} request{requestsCount === 1 ? '' : 's'}
            </div>
          </div>

          {/* CTAs — always visible. Guests get AuthGate on tap. */}
          {!mode && !isEventEnded && (
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={() => openForm('offer')}
                className="bg-cyan-600 hover:bg-cyan-500 text-white"
                size="sm"
                data-testid="civilian-offer-ride-btn"
              >
                <Car className="w-4 h-4 mr-1.5" />
                Offer a ride
              </Button>
              <Button
                onClick={() => openForm('request')}
                variant="outline"
                size="sm"
                data-testid="civilian-request-ride-btn"
              >
                <Hand className="w-4 h-4 mr-1.5" />
                Need a ride
              </Button>
            </div>
          )}

          {/* Inline form */}
          {mode && (
            <div className="rounded-lg border border-border/40 bg-background/40 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  {mode === 'offer' ? 'Offer a ride' : 'Request a ride'} {eventName ? `→ ${eventName}` : ''}
                </span>
                <button
                  type="button"
                  onClick={() => setMode(null)}
                  className="p-1 rounded hover:bg-muted"
                  aria-label="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Pickup area
                </label>
                <Input
                  placeholder="e.g. Brixton tube"
                  value={pickup}
                  onChange={(e) => setPickup(e.target.value)}
                  data-testid="civilian-ride-pickup-input"
                />
              </div>

              {mode === 'offer' ? (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Seats</label>
                    <Input
                      type="number"
                      min={1}
                      max={8}
                      value={seats}
                      onChange={(e) => setSeats(e.target.value)}
                      data-testid="civilian-ride-seats-input"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      Contribution (£)
                    </label>
                    <Input
                      type="number"
                      min={0}
                      step="0.50"
                      placeholder="optional"
                      value={contribution}
                      onChange={(e) => setContribution(e.target.value)}
                      data-testid="civilian-ride-contribution-input"
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Passengers
                  </label>
                  <Input
                    type="number"
                    min={1}
                    max={8}
                    value={passengers}
                    onChange={(e) => setPassengers(e.target.value)}
                    data-testid="civilian-ride-passengers-input"
                  />
                </div>
              )}

              <div>
                <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Notes (optional)
                </label>
                <Input
                  placeholder="anything else"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  maxLength={300}
                  data-testid="civilian-ride-notes-input"
                />
              </div>

              <Button
                onClick={submit}
                disabled={busy}
                className="w-full"
                data-testid="civilian-ride-submit-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
                Post
              </Button>
            </div>
          )}

          {/* Empty state */}
          {!loading && offers.length === 0 && requests.length === 0 && !mode && (
            <div className="text-center text-xs text-muted-foreground py-4">
              No lifts posted yet. Be the first to offer or ask.
            </div>
          )}

          {/* Drivers available */}
          {offers.length > 0 && (
            <div className="space-y-2">
              <div className="text-[11px] uppercase tracking-wide font-bold text-cyan-300/80">
                Drivers available ({offers.length})
              </div>
              {offers.map((o) => (
                <Row
                  key={o.ride_id}
                  avatarUrl={o.avatar_url}
                  name={o.driver_username || 'Skater'}
                  sub={`${o.pickup_area} · ${o.available_seats} seat${o.available_seats === 1 ? '' : 's'}${o.contribution != null ? ` · £${Number(o.contribution).toFixed(2)}` : ''}${o.notes ? ` · ${o.notes}` : ''}`}
                  right={o.driver_id === myId ? (
                    <button
                      onClick={() => remove(o.ride_id)}
                      className="text-[10px] text-rose-400 hover:underline"
                      data-testid={`civilian-ride-remove-${o.ride_id}`}
                    >
                      Remove
                    </button>
                  ) : null}
                />
              ))}
            </div>
          )}

          {/* Open requests */}
          {requests.length > 0 && (
            <div className="space-y-2">
              <div className="text-[11px] uppercase tracking-wide font-bold text-amber-300/80">
                Riders looking for a lift ({requests.length})
              </div>
              {requests.map((r) => (
                <Row
                  key={r.ride_id}
                  avatarUrl={r.avatar_url}
                  name={r.rider_username || 'Skater'}
                  sub={`${r.pickup_area} · ${r.passenger_count} pax${r.notes ? ` · ${r.notes}` : ''}`}
                  right={r.rider_id === myId ? (
                    <button
                      onClick={() => remove(r.ride_id)}
                      className="text-[10px] text-rose-400 hover:underline"
                      data-testid={`civilian-ride-remove-${r.ride_id}`}
                    >
                      Remove
                    </button>
                  ) : (
                    <MessageCircle className="w-3.5 h-3.5 text-muted-foreground" />
                  )}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <AuthGateDialog
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        title="Sign up to post a ride"
        description="Drivers and riders need an account so others can recognise and contact them. Register or log in to continue — we'll bring you straight back here."
        feature={null}
      />
    </>
  );
}
